import React from 'react';
import {
  Grid,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  ListSubheader
} from '@mui/material';
import {
  jewelryMaterialMap,
  goldColorMap,
  stoneTypeMap,
  stoneShapeMap,
  stoneCutMap,
  stonePolishMap,
  categoryMap,
  locationMap,
  collectionMap,
  stoneSymmetryMap,
  stoneColorMap,
  stoneClarityMap,
  watchMaterialMap,
  braceletMap,
  claspTypeMap,
  watchMechanismMap,
  conditionMap,
  documentsMap,
  translateValue,

  jewelryTypeMap,
  statusMap as availabilityMap,
  goldFinenessMap
} from '../../../utils/productMappings';
import { WATCH_BRANDS_MODELS_BRACELETS } from '../../../utils/watchData.js';

// Helper to render a readonly field
const renderReadonlyField = (label, value) => {
  // Nie wyświetlaj pola jeśli wartość jest pusta
  if (value === undefined || value === null || value === '') {
    return null;
  }
  
  // Sprawdzamy typ wartości i konwertujemy ją na tekst, jeśli potrzeba
  let displayValue;
  
  // Jeśli to obiekt, konwertujemy go na string
  if (typeof value === 'object' && value !== null) {
    displayValue = JSON.stringify(value);
  } else if (typeof value === 'boolean') {
    // Konwertuj wartość logiczną na 'Tak' lub 'Nie'
    displayValue = value ? 'Tak' : 'Nie';
  } else {
    // W przeciwnym razie używamy wartości jako jest
    displayValue = value;
  }
  
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{label}</Typography>
      <Typography variant="body2">{displayValue}</Typography>
    </Box>
  );
};

// Watch Details Component
const WatchDetails = ({ product, editedProduct, handleChange, handleBrandChange, handleModelChange, editMode }) => {
  const availableModels = editedProduct?.brand ? Object.keys(WATCH_BRANDS_MODELS_BRACELETS[editedProduct.brand] || {}) : [];
  const availableDiameters = (editedProduct?.brand && editedProduct?.model && WATCH_BRANDS_MODELS_BRACELETS[editedProduct.brand]?.[editedProduct.model]?.diameters) || [];
  const availableBracelets = (editedProduct?.brand && editedProduct?.model && WATCH_BRANDS_MODELS_BRACELETS[editedProduct.brand]?.[editedProduct.model]?.bracelets) || [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Szczegóły zegarka
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Marka</InputLabel><Select name="brand" value={editedProduct?.brand || ''} onChange={handleBrandChange} label="Marka">{Object.keys(WATCH_BRANDS_MODELS_BRACELETS).map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Marka", product?.brand)}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Model</InputLabel><Select name="model" value={editedProduct?.model || ''} onChange={handleModelChange} label="Model" disabled={!editedProduct?.brand}>{availableModels.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Model", product?.model)}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Średnica koperty</InputLabel><Select name="case_diameter" value={editedProduct?.case_diameter || ''} onChange={handleChange} label="Średnica koperty" disabled={!editedProduct?.model}>{availableDiameters.map(d => <MenuItem key={d} value={d}>{d} mm</MenuItem>)}</Select></FormControl> : renderReadonlyField("Średnica koperty", product?.case_diameter ? `${product.case_diameter} mm` : null)}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Typ bransolety</InputLabel><Select name="bracelet" value={editedProduct?.bracelet || ''} onChange={handleChange} label="Typ bransolety" disabled={!editedProduct?.model}>{availableBracelets.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Typ bransolety", product?.bracelet)}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Dokumenty</InputLabel><Select name="has_documents" value={editedProduct?.has_documents || ''} onChange={handleChange} label="Dokumenty"><MenuItem value={true}>Tak</MenuItem><MenuItem value={false}>Nie</MenuItem></Select></FormControl> : renderReadonlyField("Dokumenty", product?.has_documents ? 'Tak' : 'Nie')}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <TextField fullWidth margin="normal" label="Pudełko" name="box_type" value={editedProduct?.box_type || ''} onChange={handleChange} /> : renderReadonlyField("Pudełko", product?.box_type)}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Mechanizm</InputLabel><Select name="mechanism" value={editedProduct?.mechanism || ''} onChange={handleChange} label="Mechanizm">{Object.entries(watchMechanismMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Mechanizm", translateValue(product?.mechanism, watchMechanismMap))}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Diamenty</InputLabel><Select name="diamonds" value={editedProduct?.diamonds || 'no'} onChange={handleChange} label="Diamenty"><MenuItem value="no">Bez diamentów</MenuItem><MenuItem value="yes">Z diamentami</MenuItem></Select></FormControl> : renderReadonlyField("Diamenty", editedProduct?.diamonds === 'yes' ? 'Z diamentami' : 'Bez diamentów')}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <TextField fullWidth margin="normal" label="Kolor tarczy" name="dial_color" value={editedProduct?.dial_color || ''} onChange={handleChange} /> : renderReadonlyField("Kolor tarczy", product?.dial_color)}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <TextField fullWidth margin="normal" label="Referencja" name="reference_number" value={editedProduct?.reference_number || ''} onChange={handleChange} /> : renderReadonlyField("Referencja", product?.reference_number)}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <TextField fullWidth margin="normal" label="Rok produkcji" name="production_year" type="number" value={editedProduct?.production_year || ''} onChange={handleChange} /> : renderReadonlyField("Rok produkcji", product?.production_year)}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Stan</InputLabel><Select name="condition" value={editedProduct?.condition || ''} onChange={handleChange} label="Stan">{Object.entries(conditionMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Stan", translateValue(product?.condition, conditionMap))}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Dokumenty</InputLabel><Select name="documents" value={editedProduct?.documents || ''} onChange={handleChange} label="Dokumenty">{Object.entries(documentsMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Dokumenty", translateValue(product?.documents, documentsMap))}</Grid>
      <Grid item xs={12}>{editMode ? <TextField fullWidth margin="normal" label="Opis" name="description" value={editedProduct?.description || ''} onChange={handleChange} multiline rows={4} /> : renderReadonlyField("Opis", product?.description)}</Grid>
    </Grid>
  );
};

// Jewelry Details Component
const JewelryDetails = ({ product, editedProduct, handleChange, editMode }) => {

  const renderSideStoneSets = (sets) => {
    if (!sets || sets.length === 0) return null;

    return (
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          Kamienie boczne
        </Typography>
        {sets.map((set, index) => (
          <Box key={index} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Zestaw {index + 1}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Typ kamienia", translateValue(set.stone_type, stoneTypeMap))}</Grid>
              <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Kształt", translateValue(set.shape, stoneShapeMap))}</Grid>
              <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Ilość kamieni", set.side_stones_count)}</Grid>
              <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Łączna masa (ct)", set.total_weight)}</Grid>
              
              {set.stone_type === 'diamond' && (
                <>
                  <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Kolor (od)", translateValue(set.color_from, stoneColorMap))}</Grid>
                  <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Kolor (do)", translateValue(set.color_to, stoneColorMap))}</Grid>
                  <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Czystość (od)", translateValue(set.clarity_from, stoneClarityMap))}</Grid>
                  <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Czystość (do)", translateValue(set.clarity_to, stoneClarityMap))}</Grid>
                  <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Szlif", translateValue(set.cut, stoneCutMap))}</Grid>
                  <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Polerowanie", translateValue(set.polish, stonePolishMap))}</Grid>
                  <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Symetria", translateValue(set.symmetry, stoneSymmetryMap))}</Grid>
                </>
              )}

              {set.stone_type && set.stone_type !== 'diamond' && (
                 <>
                    <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Kolor", translateValue(set.color, stoneColorMap))}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Czystość", set.clarity)}</Grid>
                 </>
              )}
            </Grid>
          </Box>
        ))}
      </Grid>
    );
  };

  let sideStoneSets = [];
  // Nowa struktura danych: tablica zestawów w obiekcie side_stones
  if (product?.side_stones?.side_stones_sets?.length > 0) {
    sideStoneSets = product.side_stones.side_stones_sets;
  } 
  // Stara (płaska) struktura danych: pola bezpośrednio w produkcie
  else if (product?.has_side_stones) {
    sideStoneSets = [
      {
        stone_type: product.side_stones_type,
        shape: product.side_stones_shape,
        side_stones_count: product.side_stones_count,
        total_weight: product.side_stones_total_weight,
        color_from: product.side_stones_color_from,
        color_to: product.side_stones_color_to,
        clarity_from: product.side_stones_clarity_from,
        clarity_to: product.side_stones_clarity_to,
        cut: product.side_stones_cut,
        polish: product.side_stones_polish,
        symmetry: product.side_stones_symmetry,
      }
    ];
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Szczegóły biżuterii
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Typ biżuterii</InputLabel><Select name="jewelry_type" value={editedProduct?.jewelry_type || ''} onChange={handleChange} label="Typ biżuterii">{Object.entries(jewelryTypeMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Typ biżuterii", translateValue(product?.jewelry_type, jewelryTypeMap))}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Kolekcja</InputLabel><Select name="collection" value={editedProduct?.collection || ''} onChange={handleChange} label="Kolekcja">{Object.entries(collectionMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Kolekcja", translateValue(product?.collection, collectionMap))}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Materiał</InputLabel><Select name="material" value={editedProduct?.material || ''} onChange={handleChange} label="Materiał">{Object.entries(jewelryMaterialMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Materiał", translateValue(product?.material, jewelryMaterialMap))}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Kolor złota</InputLabel><Select name="gold_color" value={editedProduct?.gold_color || ''} onChange={handleChange} label="Kolor złota">{Object.entries(goldColorMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Kolor złota", translateValue(product?.gold_color, goldColorMap))}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <FormControl fullWidth margin="normal"><InputLabel>Próba złota</InputLabel><Select name="gold_fineness" value={editedProduct?.gold_fineness || ''} onChange={handleChange} label="Próba złota">{Object.entries(goldFinenessMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}</Select></FormControl> : renderReadonlyField("Próba złota", translateValue(product?.gold_fineness, goldFinenessMap))}</Grid>
      <Grid item xs={12} sm={6} md={4}>{editMode ? <TextField fullWidth margin="normal" label="Waga (g)" name="weight" value={editedProduct?.weight || ''} onChange={handleChange} InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }} /> : renderReadonlyField("Waga", product?.weight ? `${product.weight} g` : null)}</Grid>

      {/* Kamień centralny */}
      {product?.center_stone_type && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Kamień centralny
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Typ kamienia", translateValue(product.center_stone_type, stoneTypeMap))}</Grid>
          <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Kształt", translateValue(product.center_stone_shape, stoneShapeMap))}</Grid>
          <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Masa (ct)", product.center_stone_weight)}</Grid>
          <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Kolor", translateValue(product.center_stone_color, stoneColorMap))}</Grid>
          <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Czystość", translateValue(product.center_stone_clarity, stoneClarityMap))}</Grid>
          <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Szlif", translateValue(product.center_stone_cut, stoneCutMap))}</Grid>
          <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Polerowanie", translateValue(product.center_stone_polish, stonePolishMap))}</Grid>
          <Grid item xs={12} sm={6} md={4}>{renderReadonlyField("Symetria", translateValue(product.center_stone_symmetry, stoneSymmetryMap))}</Grid>
        </>
      )}

      {/* Render side stones consistently */}
      {renderSideStoneSets(sideStoneSets)}
      
      {/* Inne informacje */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          Inne informacje
        </Typography>
      </Grid>
      
      {(product?.jewelry_type === 'ring' || product?.jewelry_type === 'engagement_ring' || product?.ring_size || editMode) && (
        <Grid item xs={12} sm={6} md={4}>
          {editMode 
            ? <TextField fullWidth margin="normal" label="Rozmiar pierścionka" name="ring_size" value={editedProduct?.ring_size || ''} onChange={handleChange} /> 
            : renderReadonlyField("Rozmiar pierścionka", product?.ring_size)}
        </Grid>
      )}
      
      <Grid item xs={12} sm={6} md={4}>
        {editMode 
          ? <TextField fullWidth margin="normal" label="Długość" name="length_cm" 
              value={editedProduct?.length_cm || ''} 
              onChange={handleChange}
              InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }} /> 
          : renderReadonlyField("Długość", product?.length_cm ? `${product.length_cm}cm` : null)}
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        {editMode 
          ? <FormControl fullWidth margin="normal">
              <InputLabel>Typ zapięcia</InputLabel>
              <Select name="clasp_type" value={editedProduct?.clasp_type || ''} onChange={handleChange} label="Typ zapięcia">
                {Object.entries(claspTypeMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl> 
          : renderReadonlyField("Typ zapięcia", translateValue(product?.clasp_type, claspTypeMap))}
      </Grid>
      
      <Grid item xs={12}>
        {editMode 
          ? <TextField fullWidth margin="normal" label="Opis" name="description" value={editedProduct?.description || ''} onChange={handleChange} multiline rows={4} /> 
          : renderReadonlyField("Opis", product?.description)}
      </Grid>
    </Grid>
  );
};


// Main Component
const ProductDetailsInfo = ({ product, editedProduct, setEditedProduct, editMode }) => {
  if (!product) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedProduct(prev => ({ ...prev, [name]: value }));
  };

  const handleBrandChange = (e) => {
    const { value } = e.target;
    setEditedProduct(prev => ({ ...prev, brand: value, model: '', case_diameter: '' }));
  };

  const handleModelChange = (e) => {
    const { value } = e.target;
    setEditedProduct(prev => ({ ...prev, model: value, case_diameter: '', bracelet: '' }));
  };

  return (
    <Box>
      {product.category === 'watch' && (
        <WatchDetails 
          product={product} 
          editedProduct={editedProduct} 
          handleChange={handleChange} 
          handleBrandChange={handleBrandChange}
          handleModelChange={handleModelChange}
          editMode={editMode} 
        />
      )}
      {product.category !== 'watch' && (
        <JewelryDetails 
          product={product} 
          editedProduct={editedProduct} 
          handleChange={handleChange} 
          editMode={editMode} 
        />
      )}
    </Box>
  );
};

export default ProductDetailsInfo;
